import { Avatar as AntdAvatar } from 'antd';
import { useEffect, useState } from 'react';

import { type AvatarColor, DEFAULT_AVATAR_COLORS } from '@/features/user/hooks/avatarColors';
import { useUserStore } from '@/store';

export const Avatar = ({
  src,
  name,
  size,
  className,
  color,
  selectedColorId,
  availableColors = DEFAULT_AVATAR_COLORS,
  userColor,
  optimisticColorValue, // Use optimistic color instead of preview
  effectiveColorValue, // Add effective color prop
  isUpdating, // Add updating state prop
  ...props
}: {
  src?: string;
  name?: string;
  size?: number;
  color?: string;
  className?: string;
  currentUserColor?: string;
  selectedColorId?: string;
  availableColors?: AvatarColor[];
  forceRefresh?: boolean;
  userColor?: string;
  optimisticColorValue?: string | null; // Accept null values
  effectiveColorValue?: string | null; // Accept null values
  isUpdating?: boolean; // New prop
}) => {
  const [currentColorId, setCurrentColorId] = useState<string | number | undefined>(selectedColorId);

  // Obtener información del usuario para usar como fallback
  const currentUser = useUserStore((state) => state.currentUser);
  const username = currentUser?.user?.email?.split('@')[0] || '';

  // Función para obtener el color correcto con optimistic updates
  const getAvatarColor = () => {
    // Priority 1: Optimistic color (immediate feedback, maintained during updates)
    if (optimisticColorValue) {
      return optimisticColorValue;
    }

    // Priority 2: Effective color (committed or original color)
    if (effectiveColorValue) {
      return effectiveColorValue;
    }

    // Priority 3: Explicit color prop
    if (color) {
      return color;
    }

    // Priority 4: Selected color from ID
    const colorId = currentColorId || selectedColorId;
    if (colorId) {
      const selectedColor = availableColors.find((c) => c.id === colorId);
      if (selectedColor) {
        return selectedColor.value;
      }
    }

    // Priority 5: User's saved color
    if (userColor) {
      return userColor;
    }
  };

  // Función para obtener las iniciales
  const getInitials = (displayName: string): string => {
    if (!displayName) {
      return '';
    }

    const words = displayName.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }

    if (words[0].length >= 2) {
      return words[0].substring(0, 2).toUpperCase();
    }

    return words[0][0].toUpperCase();
  };

  // Minimal event handling - avoid unnecessary state changes during updates
  useEffect(() => {
    const handleAvatarColorChanged = (event: any) => {
      // Only update if not currently updating and not in optimistic state
      if (event.detail && event.detail.colorValue && !event.detail.isPreview && !isUpdating && !optimisticColorValue) {
        setCurrentColorId(event.detail.colorId);
      }
    };

    window.addEventListener('avatarColorChanged', handleAvatarColorChanged);

    return () => {
      window.removeEventListener('avatarColorChanged', handleAvatarColorChanged);
    };
  }, [isUpdating, optimisticColorValue]);

  // Prevent color changes during updates
  useEffect(() => {
    if (userColor && !selectedColorId && !isUpdating && !optimisticColorValue) {
      const backendColor = availableColors.find((c) => c.value === userColor);
      if (backendColor && backendColor.id !== currentColorId) {
        setCurrentColorId(backendColor.id);
      }
    }
  }, [userColor, selectedColorId, availableColors, currentColorId, isUpdating, optimisticColorValue]);

  useEffect(() => {
    if (selectedColorId !== undefined && selectedColorId !== currentColorId && !isUpdating) {
      setCurrentColorId(selectedColorId);
    }
  }, [selectedColorId, currentColorId, isUpdating]);

  const avatarColor = getAvatarColor();

  if (src) {
    return <AntdAvatar className={className} shape="square" size={size} src={src} {...props} />;
  }

  const displayName = name || username || '';
  const initials = getInitials(displayName);

  return (
    <AntdAvatar
      className={className}
      shape="square"
      size={size}
      style={{
        backgroundColor: avatarColor,
        color: '#ffffff',
        fontWeight: '500',
      }}
      {...props}
    >
      {initials}
    </AntdAvatar>
  );
};

export default Avatar;
